import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ExploreBackHeader, ExploreSubpageCard, useStandaloneExploreHeader } from "./areaShared";
import { getResearchDialogue } from "../../Services/research";
import {
  evidenceLabel,
  findingIconKind,
  pinFromSearch,
  safeText,
  sourceYear,
  sourceByline,
} from "./research";
import { RESEARCH_COPY } from "./copy";
import SourceSheet from "./SourceSheet";
import informedIcon from "../../Assets/icon/explore/research-informed.svg";
import usersIcon from "../../Assets/icon/explore/finding-users.svg";
import sleepIcon from "../../Assets/icon/explore/finding-sleep.svg";
import leafIcon from "../../Assets/icon/explore/finding-leaf.svg";
import heartIcon from "../../Assets/icon/explore/finding-heart.svg";
import fileIcon from "../../Assets/icon/explore/file.svg";
import infoIcon from "../../Assets/icon/explore/info.svg";
import chevronIcon from "../../Assets/icon/explore/chevron-right.svg";
import "./style.css";
import "./sections/style.css";
import "./research.css";

/**
 * Research Behind This Dialogue (Figma 10873:22838; guide §13.1, WP-W8).
 *
 * Renders only what `GET /ARIS/v4/research/dialogues/{id}` returns — the whole
 * guarantee is that a reviewer approved this exact text (§13.6 rule 6):
 *
 * - Each finding shows `headline` bold, then `finding`. `material_limitations`
 *   sits directly under the findings, every time; findings are never drawn
 *   without it (in library v23 `do_not_infer` is always "").
 * - `external_review_disclosure` is rendered whenever present.
 * - `content_gaps[]` and any `⚠ PLACEHOLDER` string never reach the page.
 * - A source's year is `year ?? year_label`; `publisher_key` is never printed
 *   (no logo assets ship for it yet, so the institution name stands in).
 */

const FINDING_ICON = {
  users: { src: usersIcon, tone: "teal" },
  sleep: { src: sleepIcon, tone: "blue" },
  leaf: { src: leafIcon, tone: "teal" },
  heart: { src: heartIcon, tone: "orange" },
  neutral: { src: fileIcon, tone: "blue" },
};

function ResearchPage() {
  const { researchDialogueId } = useParams();
  const [searchParams] = useSearchParams();
  useStandaloneExploreHeader();

  const [state, setState] = useState({ loading: true, data: null, missing: false });
  const [openSource, setOpenSource] = useState(null);
  const pin = pinFromSearch(searchParams);
  const pinKey = JSON.stringify(pin);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, missing: false });
    (async () => {
      try {
        const data = await getResearchDialogue(researchDialogueId, JSON.parse(pinKey));
        if (!cancelled) setState({ loading: false, data, missing: !data });
      } catch (e) {
        if (e?.response?.status !== 404) console.error("Failed to load the research", e);
        if (!cancelled) setState({ loading: false, data: null, missing: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [researchDialogueId, pinKey]);

  const d = state.data;
  const findings = (d?.what_the_research_tells_us || []).filter(
    (f) => safeText(f?.finding) || safeText(f?.headline)
  );
  const limitations = safeText(d?.material_limitations);
  const sources = (d?.research_and_sources || []).filter((s) => s?.source_id);

  return (
    <div className="explore-page exr">
      <div className="explore-page__inner">
        <ExploreBackHeader to={-1} />
        <ExploreSubpageCard>
          <header className="exr-header">
            <h1 className="explore-page__title exr-header__title">{RESEARCH_COPY.title}</h1>
            <p className="exr-header__sub">{RESEARCH_COPY.subtitle}</p>
          </header>

          {state.loading && <div className="explore-card explore-card--status">Loading…</div>}
          {!state.loading && state.missing && (
            <div className="explore-card explore-card--status">{RESEARCH_COPY.unavailable}</div>
          )}

          {d && (
            <>
              {safeText(d.how_this_dialogue_is_informed) && (
                <section className="exr-callout">
                  <span className="exr-callout__tile">
                    <img src={informedIcon} alt="" width="24" height="24" />
                  </span>
                  <div>
                    <h2 className="exr-callout__title">{RESEARCH_COPY.informed}</h2>
                    <p className="exr-callout__body">{d.how_this_dialogue_is_informed}</p>
                  </div>
                </section>
              )}

              {safeText(d.external_review_disclosure) && (
                <p className="exr-disclosure">{d.external_review_disclosure}</p>
              )}

              {findings.length > 0 && (
                <section className="ex-section">
                  <h2 className="ex-section__title">{RESEARCH_COPY.findings}</h2>
                  <div className="ex-plist">
                    {findings.map((f, i) => {
                      const look = FINDING_ICON[findingIconKind(f.icon)];
                      return (
                        <div key={i} className="ex-plist__row exr-finding">
                          <span className={`ex-plist__tile ex-plist__tile--${look.tone}`}>
                            <img src={look.src} alt="" width="24" height="24" />
                          </span>
                          <span className="ex-plist__text">
                            {/* v26.18: a short heading and its supporting sentence, once
                                each; older releases sent the same text in both. */}
                            {safeText(f.headline) && (
                              <span className="ex-plist__title exr-finding__heading">
                                {f.headline}
                              </span>
                            )}
                            {safeText(f.finding) && f.finding !== f.headline && (
                              <span className="ex-plist__sub exr-finding__body">{f.finding}</span>
                            )}
                            {safeText(f.do_not_infer) && (
                              <span className="ex-plist__sub exr-finding__limit">
                                {f.do_not_infer}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {limitations && (
                    <p className="exr-limitations">
                      <strong>{RESEARCH_COPY.limitations}</strong> {limitations}
                    </p>
                  )}
                </section>
              )}

              {safeText(d.how_aris_uses_this_research) && (
                <section className="exr-card">
                  <h2 className="exr-card__title">{RESEARCH_COPY.howAris}</h2>
                  <p className="exr-card__body">{d.how_aris_uses_this_research}</p>
                </section>
              )}

              {sources.length > 0 && (
                <section className="ex-section">
                  <h2 className="ex-section__title">
                    {RESEARCH_COPY.sources(sources.length)}
                  </h2>
                  <div className="ex-plist">
                    {sources.map((s) => {
                      const meta = [evidenceLabel(s.evidence_type), sourceYear(s)]
                        .filter(Boolean)
                        .join(" • ");
                      return (
                        <button
                          key={s.source_id}
                          type="button"
                          className="ex-plist__row"
                          onClick={() => setOpenSource(s)}
                        >
                          <span className="ex-plist__text">
                            {sourceByline(s) && (
                              <span className="ex-plist__title">{sourceByline(s)}</span>
                            )}
                            {s.title && <span className="ex-plist__sub">{s.title}</span>}
                            {meta && <span className="ex-plist__sub">{meta}</span>}
                          </span>
                          <span className="ex-plist__chevron">
                            <img src={chevronIcon} alt="" width="16" height="16" />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {safeText(d.about_our_research) && (
                <section className="exr-callout exr-callout--info">
                  <img src={infoIcon} alt="" width="24" height="24" />
                  <div>
                    <h2 className="exr-callout__title">{RESEARCH_COPY.about}</h2>
                    <p className="exr-callout__body">{d.about_our_research}</p>
                  </div>
                </section>
              )}
            </>
          )}
        </ExploreSubpageCard>
      </div>

      <SourceSheet
        source={openSource}
        dialogueId={d?.dialogue_id || pin.dialogue_id}
        libraryVersion={d?.library_version || pin.library_version}
        onClose={() => setOpenSource(null)}
      />
    </div>
  );
}

export default ResearchPage;
